import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { sendTransactionSync } from '@wagmi/core'
import { type Provider, Rpc } from 'accounts'
import { Bytes, Hex, RpcResponse } from 'ox'
import { useEffect, useMemo, useState } from 'react'
import {
  erc20Abi,
  encodeFunctionData,
  formatUnits,
  isAddress,
  parseUnits,
  type Address,
} from 'viem'
import { tempo, tempoModerato } from 'viem/chains'
import { Actions, Addresses } from 'viem/tempo'
import { useConnection, usePrepareTransactionRequest } from 'wagmi'
import { Hooks } from 'wagmi/tempo'
import * as z from 'zod/mini'

import { useAssets } from '#/lib/assets.js'
import { wagmiConfig } from '#/lib/config.js'
import { useWebAuthnConnector } from '#/lib/connectors.js'
import { useDebounce } from '#/lib/react.js'
import { useAnalytics } from '#/lib/telemetry/analytics.js'
import * as SentryBrowser from '#/lib/telemetry/sentry-browser.js'
import { formatQuantity, isTip20 } from '#/lib/token.js'
import { useTokens } from '#/lib/tokens.js'
import { revertedTxHash } from '#/lib/transaction.js'
import * as Zone from '#/lib/zone.js'
import * as TransactionFrames from '../-frames/transaction/index.js'
import { usePrepareAlert } from '../-hooks/usePrepareAlert.js'
import { useRequireFunds } from '../-hooks/useRequireFunds.js'
import * as RecipientPolicy from '../-lib/RecipientPolicy.js'
import * as RemoteRpc from '../-lib/RemoteRpc.js'
import * as SameSite from '../-lib/SameSite.js'
import * as VirtualAddress from '../-lib/VirtualAddress.js'

const decimalRegex = /^\d*\.?\d*$/

/** Maximum memo length in UTF-8 bytes (TIP20 `transferWithMemo` accepts a `bytes32`). */
const memoMaxBytes = 32

export const Route = createFileRoute('/remote/rpc/wallet_transfer')({
  component: Component,
  validateSearch: (search) => RemoteRpc.validateSearch(search, { method: 'wallet_transfer' }),
})

function Component() {
  const { capture } = useAnalytics()
  const request = Route.useSearch()
  const rpc = RemoteRpc.use()
  const { address, chainId } = useConnection()
  const webauthnConnector = useWebAuthnConnector()

  const params = request._decoded?.params?.[0]
  const parentChainId = chainId ?? tempo.id
  const isModerato = parentChainId === tempoModerato.id
  const feeTokenPreference = useQuery({
    enabled: !!address,
    queryFn: () =>
      Actions.fee
        .getUserToken(wagmiConfig.getClient({ chainId: parentChainId as typeof tempo.id }), {
          account: address!,
        })
        .catch(() => null),
    queryKey: ['feeTokenPreference', parentChainId, address],
    staleTime: 60_000,
  })

  const zoneAuth = Zone.useAuthToken()
  const zoneUnlocked = !!zoneAuth.data
  const [source, setSource] = useState<TransactionFrames.Send.Source>('public')
  const isPrivate = zoneUnlocked && source === 'private'
  const resolvedChainId = isPrivate ? Zone.chain.id : parentChainId

  const [values, setValues] = useState(() => ({
    // Pre-fill the memo input from `params.memo`. Validation against
    // the resolved token's TIP-20 status happens once both
    // `params.token` and the curated tokenlist resolve (see below).
    note: params?.memo ?? '',
    to: params?.to ?? '',
    // Symbol-form `params.token` is resolved against the curated
    // tokenlist via `resolvedParamsToken` once it loads — keep this
    // input empty so the user-set value branch in `effectiveToken`
    // doesn't shortcut the resolution.
    token: '',
    value: params?.amount ?? '',
  }))

  const to = values.to.trim()
  const value = values.value.trim()
  const note = values.note
  const recipientValid = isAddress(to)
  const recipientBlocked = RecipientPolicy.isBlocked(to)

  const recipientError = useMemo(() => {
    if (!to) return undefined
    if (!recipientValid) return 'Enter a valid address.'
    if (recipientBlocked) return 'Unable to send to this recipient.'
    return undefined
  }, [recipientBlocked, recipientValid, to])

  useEffect(() => {
    if (!recipientBlocked) return
    RecipientPolicy.report({
      method: request.method,
      origin: rpc.consumer.origin,
      recipient: to,
    })
  }, [recipientBlocked, request.method, rpc.consumer.origin, to])

  const assets = useAssets({ account: address, chainId: parentChainId })
  const tokens = useTokens({ chainId: parentChainId })

  // Sortable, sendable subset (verified) — keep any token whose
  // gross holdings (parent + every zone) exceed 0 so we don't drop
  // private-only positions from the dropdown when the user switches
  // the source segmented control to "Private".
  const sendableAssets = useMemo(() => {
    if (!assets.data) return undefined
    return assets.data
      .filter((a) => {
        if (a.verified === false) return false
        const zoneTotal = a.zones.reduce((sum, z) => sum + BigInt(z.balance), 0n)
        return BigInt(a.balance) + zoneTotal > 0n
      })
      .slice()
      .sort((a, b) => {
        const af = a.fiat ? Number.parseFloat(a.fiat.balance) : -1
        const bf = b.fiat ? Number.parseFloat(b.fiat.balance) : -1
        return bf - af
      })
  }, [assets.data])

  // Curated tokenlist tokens not currently held — surfaced under the
  // held section so users can pick an asset and fund it ad-hoc via
  // autoSwap or onramp without first depositing it.
  const curatedTokens = useMemo(() => {
    if (!tokens.data) return undefined
    const heldByAddress = new Set(sendableAssets?.map((a) => a.address.toLowerCase()) ?? [])
    return tokens.data.filter((t) => !heldByAddress.has(t.address.toLowerCase()))
  }, [sendableAssets, tokens.data])

  // Resolve `params.token` (address or curated tokenlist symbol).
  // `ready` flips true once a determinate result is available so the
  // rejection effect below can fire deterministically (we don't want
  // to reject before the curated tokenlist has had a chance to load).
  const paramsToken = params?.token
  const resolvedParamsToken = useMemo<{
    address: Hex.Hex | undefined
    ready: boolean
  }>(() => {
    if (!paramsToken) return { address: undefined, ready: true }
    if (isAddress(paramsToken)) return { address: paramsToken as Hex.Hex, ready: true }
    if (!tokens.data) return { address: undefined, ready: false }
    const lowered = paramsToken.toLowerCase()
    const found = tokens.data.find((t) => t.symbol.toLowerCase() === lowered)
    return { address: found?.address as Hex.Hex | undefined, ready: true }
  }, [paramsToken, tokens.data])

  // Reject the request when the dapp-supplied params can't be honored:
  //  - `token` symbol that doesn't match any curated tokenlist entry
  //  - `memo` provided alongside a non-TIP-20 token (memos are only
  //    representable in TIP-20 transfers).
  useEffect(() => {
    if (!resolvedParamsToken.ready) return
    if (paramsToken && !isAddress(paramsToken) && !resolvedParamsToken.address) {
      rpc.reject(
        new RpcResponse.InvalidParamsError({
          message: `Unknown token symbol "${paramsToken}".`,
        }),
      )
      return
    }
    if (params?.memo && resolvedParamsToken.address && !isTip20(resolvedParamsToken.address))
      rpc.reject(
        new RpcResponse.InvalidParamsError({
          message: '`memo` is only supported for TIP-20 token transfers.',
        }),
      )
  }, [params?.memo, paramsToken, resolvedParamsToken, rpc])

  // Effective token address: user-set value wins, otherwise prefer the
  // dapp-resolved token even when not held (curated/metadata fallback
  // handles display), then fall back to the user's highest-fiat asset.
  const effectiveToken = useMemo(() => {
    if (values.token) return values.token
    if (resolvedParamsToken.address) return resolvedParamsToken.address
    if (!sendableAssets || sendableAssets.length === 0) return ''
    return sendableAssets[0].address
  }, [resolvedParamsToken.address, sendableAssets, values.token])

  const heldAsset = useMemo(() => {
    if (!effectiveToken || !sendableAssets) return undefined
    return sendableAssets.find((a) => a.address.toLowerCase() === effectiveToken.toLowerCase())
  }, [sendableAssets, effectiveToken])

  // Curated metadata for the selected token when not held — saves an
  // extra on-chain `getMetadata` round-trip for tokenlist tokens.
  const curatedAsset = useMemo(() => {
    if (!effectiveToken || heldAsset || !curatedTokens) return undefined
    return curatedTokens.find((t) => t.address.toLowerCase() === effectiveToken.toLowerCase())
  }, [curatedTokens, effectiveToken, heldAsset])

  // Fallback metadata for tokens the user doesn't currently hold and that
  // aren't in the curated tokenlist (e.g. when the dapp pre-selects an
  // arbitrary token via params.token and we'll source it via autoSwap or
  // onramp). Only fetched when needed.
  const tokenIsAddress = isAddress(effectiveToken)
  const metadata = Hooks.token.useGetMetadata({
    token: effectiveToken as Hex.Hex,
    query: {
      enabled: tokenIsAddress && !heldAsset && !curatedAsset && !assets.isLoading,
    },
  })

  // Private balance for the currently-selected asset on the active zone
  // (raw token units, 0 if absent). The Private source option is gated
  // on this being non-zero.
  const heldAssetPrivateBalance = useMemo(() => {
    if (!heldAsset) return 0n
    const entry = heldAsset.zones.find((z) => z.zoneId === Zone.zoneId)
    return entry ? BigInt(entry.balance) : 0n
  }, [heldAsset])
  // Zones only hold TIP-20 tokens, so the Private source option is only
  // meaningful for TIP-20 assets even if a non-TIP20 row sneaks in via
  // the tokenlist.
  const privateBalanceAvailable =
    zoneUnlocked && !!heldAsset && isTip20(heldAsset.address) && heldAssetPrivateBalance > 0n

  // If the user picked the private source and then switches to a token
  // without zone holdings, the segmented control vanishes — fall back
  // to public so we don't silently keep routing through the zone path.
  useEffect(() => {
    if (source === 'private' && !privateBalanceAvailable) setSource('public')
  }, [source, privateBalanceAvailable])

  const selectedAsset = useMemo(() => {
    if (heldAsset)
      return {
        address: heldAsset.address,
        // Display the active source's balance so MAX, "available" and
        // amount validation all key off the same number.
        balance: isPrivate ? heldAssetPrivateBalance : BigInt(heldAsset.balance),
        currency: heldAsset.fiat?.currency,
        decimals: heldAsset.decimals,
        symbol: heldAsset.symbol,
      }
    if (curatedAsset)
      return {
        address: curatedAsset.address,
        balance: 0n,
        currency: 'USD',
        decimals: curatedAsset.decimals,
        symbol: curatedAsset.symbol,
      }
    if (!tokenIsAddress || !metadata.data) return undefined
    return {
      address: effectiveToken as Hex.Hex,
      balance: 0n,
      currency: metadata.data.currency,
      decimals: metadata.data.decimals,
      symbol: metadata.data.symbol,
    }
  }, [
    curatedAsset,
    effectiveToken,
    heldAsset,
    heldAssetPrivateBalance,
    isPrivate,
    metadata.data,
    tokenIsAddress,
  ])

  const tokenError = useMemo(() => {
    if (!effectiveToken) return undefined
    if (!tokenIsAddress) return 'Invalid asset.'
    if (metadata.isError) return 'Unable to load token details.'
    return undefined
  }, [effectiveToken, metadata.isError, tokenIsAddress])

  // Preserve Tempo's normal fee-token inference for USD transfers. Only
  // override it when the transferred asset cannot itself pay network fees.
  const feeToken =
    selectedAsset && selectedAsset.currency !== 'USD'
      ? (feeTokenPreference.data?.address ?? Addresses.pathUsd)
      : undefined

  const amount = useMemo<bigint | undefined>(() => {
    if (!value || !selectedAsset || value === '.') return undefined
    // Normalize bare "1." → "1" and ".5" → "0.5" so partial decimal entries parse.
    const normalized = value.startsWith('.')
      ? `0${value}`
      : value.endsWith('.')
        ? value.slice(0, -1)
        : value
    const decimals = normalized.split('.')[1]?.length ?? 0
    if (decimals > selectedAsset.decimals) return undefined
    try {
      return parseUnits(normalized, selectedAsset.decimals)
    } catch {
      return undefined
    }
  }, [selectedAsset, value])

  // Note: insufficient-balance is no longer surfaced here — we still run prepare
  // so the relay can offer an autoSwap or surface a `requireFunds` capability.
  const valueError = useMemo(() => {
    if (!value) return undefined
    if (!decimalRegex.test(value) || value === '.') return 'Enter a valid amount.'
    if (!selectedAsset) return undefined
    const decimals = value.split('.')[1]?.length ?? 0
    if (decimals > selectedAsset.decimals) return `Use at most ${selectedAsset.decimals} decimals.`
    if (amount === undefined) return 'Enter a valid amount.'
    if (amount <= 0n) return 'Enter an amount greater than 0.'
    return undefined
  }, [amount, selectedAsset, value])

  const noteBytes = useMemo(() => Bytes.size(Bytes.fromString(note)), [note])
  const noteError =
    noteBytes > memoMaxBytes ? `Notes are limited to ${memoMaxBytes} bytes.` : undefined
  const memoSupported = !!selectedAsset && isTip20(selectedAsset.address)

  const calls = useMemo(() => {
    if (!recipientValid) return undefined
    if (!selectedAsset || amount === undefined || amount <= 0n) return undefined
    if (noteBytes > memoMaxBytes) return undefined
    const toAddr = to as Hex.Hex
    const tokenAddr = selectedAsset.address as Hex.Hex
    if (isTip20(tokenAddr)) {
      const memo = note && memoSupported ? Hex.fromString(note, { size: 32 }) : undefined
      return [
        Actions.token.transfer.call({
          amount,
          memo,
          to: toAddr,
          token: tokenAddr,
        }),
      ]
    }
    const data = encodeFunctionData({
      abi: erc20Abi,
      args: [toAddr, amount],
      functionName: 'transfer',
    })
    return [{ data, to: tokenAddr, value: 0n }]
  }, [amount, memoSupported, note, noteBytes, recipientValid, selectedAsset, to])

  // Debounce so we don't issue a relay prepare on every keystroke.
  const debouncedCalls = useDebounce(calls, 300)
  // Cross-site apps don't get the wallet's relay-funded sponsorship.
  // App-supplied fee payer addresses/URLs (strings) are still respected.
  const sameSite = SameSite.useValidate(rpc.consumer.origin)
  const feePayer = sameSite || typeof params?.feePayer === 'string' ? params?.feePayer : false

  const prepare = usePrepareTransactionRequest({
    calls: debouncedCalls,
    // Skip the relay's post-fill `tempo_simulateV1` (~250-400ms). The
    // `balanceDiffs` capability is unused in this UI.
    capabilities: { balanceDiffs: false, errors: true },
    chainId: parentChainId as typeof tempo.id,
    feeToken,
    feePayer: feePayer as never,
    type: 'tempo',
    query: {
      // Private sends bypass the relay and submit directly to the zone
      // RPC, so disable prepare entirely in that mode.
      enabled: !isPrivate && !!address && !!debouncedCalls,
      refetchOnWindowFocus: false,
      // Poll while waiting for funds to arrive (deposit/onramp) so the
      // requireFunds capability clears as soon as a deposit confirms.
      refetchInterval: (query) => (query.state.data?._capabilities?.requireFunds ? 2_000 : false),
      retry: false,
      staleTime: 0,
      gcTime: 0,
    },
  })

  const prepareData = prepare.data
  const capabilities = prepare.data?._capabilities
  const preparingCurrentInput = calls !== debouncedCalls || prepare.isFetching
  const virtualAddresses = preparingCurrentInput
    ? undefined
    : VirtualAddress.fromCapabilities(capabilities)
  const virtualAddressMaster = recipientValid
    ? VirtualAddress.resolve(virtualAddresses, to as Address)
    : undefined
  const virtualAddressAlert = useMemo(() => {
    if (virtualAddressMaster === undefined) return null
    if (!memoSupported)
      return {
        type: 'error',
        message: 'Non-TIP-20 tokens sent to virtual addresses are unrecoverable.',
      } as const
    return null
  }, [memoSupported, virtualAddressMaster])
  const forwardedAddress = memoSupported ? (virtualAddressMaster ?? undefined) : undefined
  const autoSwap = capabilities?.autoSwap

  const funds = useRequireFunds({
    address,
    chainId: resolvedChainId,
    prepareQuery: prepare,
  })
  const requireFunds = funds.requireFunds

  const confirm = useMutation({
    mutationFn: async () => {
      if (isPrivate) {
        if (!recipientValid) throw new Error('Enter a valid recipient.')
        if (!selectedAsset || amount === undefined || amount <= 0n)
          throw new Error('Enter an amount.')
        if (noteBytes > memoMaxBytes) throw new Error('Note too long.')
        const provider = (await webauthnConnector.getProvider()) as Provider.Provider
        const account = provider.getAccount()
        if (!account) throw new Error('No signable account.')
        const zoneClient = wagmiConfig.getClient({ chainId: Zone.chain.id })
        const memo = note && memoSupported ? Hex.fromString(note, { size: 32 }) : undefined
        const result = await Actions.token.transferSync(zoneClient, {
          account: account as never,
          amount,
          to,
          token: selectedAsset.address,
          ...(memo && { memo }),
        })
        const receipt = result.receipt
        await rpc.resolve({
          chainId: zoneClient.chain.id,
          receipt: z.encode(Rpc.receipt as never, receipt as never),
        })
        return receipt
      }

      if (preparingCurrentInput) throw new Error('Transaction not prepared for current inputs.')
      if (!prepareData) throw new Error('Transaction not prepared.')
      // If the relay quote includes autoSwap calls, prepend them so the
      // missing funds are sourced from the user's other balances atomically.
      const swapCalls = autoSwap?.calls
      const data = swapCalls
        ? { ...prepareData, calls: [...swapCalls, ...(prepareData.calls ?? [])] }
        : prepareData
      const receipt = await sendTransactionSync(wagmiConfig, data as never)
      await rpc.resolve({
        chainId: parentChainId,
        receipt: z.encode(Rpc.receipt as never, receipt as never),
      })
      return receipt
    },
    onSuccess: (receipt) =>
      capture('send_success', {
        chainId: resolvedChainId,
        method: request.method,
        txHash: receipt.transactionHash,
      }),
    onError: (error) => {
      void SentryBrowser.captureException(error)
      const txHash = revertedTxHash(error)
      capture('send_failure', {
        chainId: resolvedChainId,
        error: error.message,
        errorName: error.name,
        method: request.method,
        phase: 'confirm',
        ...(txHash ? { txHash } : {}),
      })
    },
  })

  // Fire once when the route mounts.
  useEffect(() => {
    capture('send_started')
  }, [])

  // Fire when prepare flips into error state. We intentionally key only on
  // isError so a single failure surface fires exactly once per failed query.
  useEffect(() => {
    if (!prepare.isError) return
    void SentryBrowser.captureException(prepare.error)
    capture('send_simulation_failed', {
      chainId: resolvedChainId,
      error: prepare.error.message,
      errorName: prepare.error.name,
      method: request.method,
      phase: 'prepare',
    })
  }, [prepare.isError])

  const confirmTxHash = confirm.error ? revertedTxHash(confirm.error) : undefined

  function reject() {
    capture('send_dismissed')
    rpc.reject()
  }

  function submit() {
    if (virtualAddressAlert) return
    if (confirm.error)
      capture('send_retry_clicked', {
        chainId: resolvedChainId,
        errorName: confirm.error.name,
        method: request.method,
        phase: 'confirm',
        ...(confirmTxHash ? { txHash: confirmTxHash } : {}),
      })
    capture('send_submitted', { chainId: resolvedChainId, method: request.method })
    confirm.mutate()
  }

  // Asset rows for the dropdown — held assets first (sorted by fiat),
  // followed by curated tokenlist tokens the user doesn't yet hold so
  // they can pick one and fund it ad-hoc via autoSwap or onramp. Shape
  // kept narrow so the frame stays presentational.
  const assetOptions = useMemo(() => {
    if (!sendableAssets && !curatedTokens) return undefined
    const held = (sendableAssets ?? []).map((a) => ({
      address: a.address,
      balance: formatQuantity(BigInt(a.balance), a.decimals),
      symbol: a.symbol,
    }))
    const curated = (curatedTokens ?? []).map((t) => ({
      address: t.address,
      // Empty balance = no suffix in the dropdown row, visually
      // distinguishing curated tokens from held ones.
      balance: '',
      symbol: t.symbol,
    }))
    return [...held, ...curated]
  }, [curatedTokens, sendableAssets])

  // Treat first-party requests (e.g. the dashboard sending via the iframe)
  // as host-less so the UI shows the generic copy.
  const host = RemoteRpc.useExternalHost()

  const primaryLabel = useMemo(() => {
    if (confirm.isError) return 'Try again'
    if (confirm.isPending) return 'Sending…'
    return 'Send'
  }, [confirm.isError, confirm.isPending])

  const alert = usePrepareAlert({
    mutation: {
      action: confirmTxHash
        ? {
            href: receiptUrl(resolvedChainId, confirmTxHash),
            label: 'View',
            onClick: () =>
              capture('send_error_view_explorer_clicked', {
                chainId: resolvedChainId,
                errorName: confirm.error?.name,
                method: request.method,
                phase: 'confirm',
                txHash: confirmTxHash,
              }),
          }
        : undefined,
      error: confirm.error,
    },
    query: prepare,
    suppress: !!(
      recipientError ||
      tokenError ||
      valueError ||
      noteError ||
      virtualAddressAlert ||
      calls !== debouncedCalls
    ),
  })
  const frameAlert = virtualAddressAlert ?? alert

  if (funds.frame) return funds.frame

  const { fee, sponsor, sponsored: sponsoredCap } = capabilities ?? {}
  const sponsored = !!sponsor || !!sponsoredCap

  const inputsValid =
    !!to &&
    !!selectedAsset &&
    !!value &&
    !recipientError &&
    !tokenError &&
    !valueError &&
    !noteError

  // Private mode skips the relay prepare entirely, so don't render a
  // "fee pending" placeholder for it.
  const feePending = !isPrivate && inputsValid && (assets.isLoading || prepare.isFetching)

  return (
    <TransactionFrames.Send
      actions={funds.actions}
      alert={frameAlert}
      assets={assetOptions}
      assetsLoading={assets.isLoading}
      autoSwap={autoSwap}
      chainId={resolvedChainId}
      confirming={confirm.isPending}
      errors={{ note: noteError, to: recipientError, token: tokenError, value: valueError }}
      fee={fee}
      feePending={feePending && !fee}
      forwardedAddress={forwardedAddress}
      host={host}
      memoSupported={memoSupported}
      noteBytes={noteBytes}
      noteMaxBytes={memoMaxBytes}
      onChange={(event) => {
        confirm.reset()
        if (event.field === 'value') {
          const next = event.value.replaceAll(',', '.')
          if (!decimalRegex.test(next)) return
          setValues((current) => ({ ...current, value: next }))
          return
        }
        setValues((current) => ({ ...current, [event.field]: event.value }))
      }}
      onConfirm={submit}
      onMax={
        selectedAsset && selectedAsset.balance > 0n
          ? () => {
              confirm.reset()
              setValues((current) => ({
                ...current,
                value: formatUnits(selectedAsset.balance, selectedAsset.decimals),
              }))
            }
          : undefined
      }
      onRandomRecipient={
        isModerato
          ? () => {
              confirm.reset()
              setValues((current) => ({ ...current, to: Hex.random(20) }))
            }
          : undefined
      }
      onReject={reject}
      onSourceChange={
        privateBalanceAvailable
          ? (next) => {
              if (next === source) return
              confirm.reset()
              setSource(next)
              // Reset the amount: the active balance changes underfoot,
              // so any value the user typed against the public balance
              // can become invalid against the (smaller) private one.
              setValues((current) => ({ ...current, value: '' }))
            }
          : undefined
      }
      passkey={!confirm.isPending || confirm.isError}
      primaryLabel={primaryLabel}
      ready={
        isPrivate
          ? inputsValid && !virtualAddressAlert && !confirm.isPending
          : !!prepareData &&
            !preparingCurrentInput &&
            !requireFunds &&
            !capabilities?.error &&
            !virtualAddressAlert
      }
      selectedAsset={
        selectedAsset
          ? {
              address: selectedAsset.address,
              balance: formatQuantity(selectedAsset.balance, selectedAsset.decimals),
              symbol: selectedAsset.symbol,
            }
          : undefined
      }
      source={source}
      sponsored={sponsored}
      values={{ ...values, token: effectiveToken }}
    />
  )
}

function receiptUrl(chainId: number, hash: Hex.Hex) {
  const host = chainId === tempoModerato.id ? 'explore.moderato.tempo.xyz' : 'explorer.tempo.xyz'
  return `https://${host}/receipt/${hash}`
}
